import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, subWeeks, parseISO } from 'date-fns';
import { ja } from 'date-fns/locale';
import { type Entry } from '@/lib/api';
import { moodOptions } from '@/types';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import AnimatedCounter from '@/components/review/AnimatedCounter';

// ── Tabs ──────────────────────────────────────────────
type Tab = 'sentiment' | 'mood';

// ── Sentiment view mode ───────────────────────────────
type ViewMode = '1week' | '2weeks' | '4weeks';
const VIEWS: { key: ViewMode; label: string; weeks: number }[] = [
  { key: '1week', label: '1w', weeks: 1 },
  { key: '2weeks', label: '2w', weeks: 2 },
  { key: '4weeks', label: '4w', weeks: 4 },
];

// ── Donut colors ──────────────────────────────────────
const DONUT_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#F43F5E', '#8B5CF6', '#06B6D4', '#F97316', '#EC4899'];

interface Props {
  entries: Entry[];
}

export default function SentimentAreaChart({ entries }: Props) {
  const [tab, setTab] = useState<Tab>('sentiment');
  const [viewMode, setViewMode] = useState<ViewMode>('2weeks');

  return (
    <Card className="rounded-xl shadow-sm">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          {/* Tab switcher */}
          <div className="flex gap-0.5 bg-muted rounded-lg p-0.5">
            <button
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                tab === 'sentiment' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'
              }`}
              onClick={() => setTab('sentiment')}
            >
              感情の推移
            </button>
            <button
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                tab === 'mood' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'
              }`}
              onClick={() => setTab('mood')}
            >
              気分の分布
            </button>
          </div>

          {/* Period toggle (shared) */}
          <div className="flex gap-0.5">
            {VIEWS.map(v => (
              <Button
                key={v.key}
                size="sm"
                variant={viewMode === v.key ? 'default' : 'ghost'}
                className="h-7 px-2 text-xs"
                onClick={() => setViewMode(v.key)}
              >
                {v.label}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-2 sm:px-6">
        <AnimatePresence mode="wait">
          {tab === 'sentiment' ? (
            <motion.div
              key="sentiment"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <SentimentPanel entries={entries} viewMode={viewMode} />
            </motion.div>
          ) : (
            <motion.div
              key="mood"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <MoodPanel entries={entries} viewMode={viewMode} />
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}

// ── Sentiment Area Chart Panel ────────────────────────
function SentimentPanel({ entries, viewMode }: { entries: Entry[]; viewMode: ViewMode }) {
  const data = useMemo(() => {
    const view = VIEWS.find(v => v.key === viewMode)!;
    const cutoff = format(subWeeks(new Date(), view.weeks), 'yyyy-MM-dd');

    const scoresByDate: Record<string, number[]> = {};
    for (const e of entries) {
      if (e.sentiment_score !== undefined && e.date && e.date >= cutoff) {
        if (!scoresByDate[e.date]) scoresByDate[e.date] = [];
        scoresByDate[e.date].push(e.sentiment_score);
      }
    }

    return Object.entries(scoresByDate)
      .map(([date, scores]) => ({
        date,
        score: scores.reduce((a, b) => a + b, 0) / scores.length,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [entries, viewMode]);

  if (data.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">
        日記を記録すると感情の変化がここに表示されます
      </p>
    );
  }

  const padding = { top: 16, right: 12, bottom: 28, left: 32 };
  const width = 400;
  const height = 160;
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;
  const minY = -1;
  const maxY = 1;

  const toX = (i: number) =>
    padding.left + (data.length === 1 ? chartW / 2 : (i / (data.length - 1)) * chartW);
  const toY = (score: number) =>
    padding.top + chartH - ((score - minY) / (maxY - minY)) * chartH;

  const points = data.map((d, i) => ({ x: toX(i), y: toY(d.score) }));
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const baseY = toY(Math.max(minY, Math.min(0, maxY)));
  const areaD = `${pathD} L ${points[points.length - 1].x} ${baseY} L ${points[0].x} ${baseY} Z`;

  const yTicks = [
    { value: 1, label: '+' },
    { value: 0, label: '0' },
    { value: -1, label: '-' },
  ];

  const xLabels: Array<{ i: number; label: string }> = [];
  if (data.length >= 1)
    xLabels.push({ i: 0, label: format(parseISO(data[0].date), 'M/d', { locale: ja }) });
  if (data.length >= 3) {
    const mid = Math.floor(data.length / 2);
    xLabels.push({ i: mid, label: format(parseISO(data[mid].date), 'M/d', { locale: ja }) });
  }
  if (data.length >= 2)
    xLabels.push({
      i: data.length - 1,
      label: format(parseISO(data[data.length - 1].date), 'M/d', { locale: ja }),
    });

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto">
      <line
        x1={padding.left} y1={toY(0)}
        x2={width - padding.right} y2={toY(0)}
        stroke="currentColor" strokeOpacity={0.12} strokeDasharray="4 4"
      />
      {yTicks.map(tick => (
        <text key={tick.value} x={padding.left - 8} y={toY(tick.value) + 4}
          textAnchor="end" className="fill-muted-foreground" fontSize={10}>
          {tick.label}
        </text>
      ))}
      {xLabels.map(({ i, label }) => (
        <text key={i} x={toX(i)} y={height - 4}
          textAnchor="middle" className="fill-muted-foreground" fontSize={10}>
          {label}
        </text>
      ))}
      <defs>
        <linearGradient id="dashSentimentGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
        </linearGradient>
      </defs>
      <motion.path
        key={viewMode + '-area'}
        d={areaD} fill="url(#dashSentimentGrad)"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      />
      <motion.path
        key={viewMode + '-line'}
        d={pathD} fill="none" stroke="hsl(var(--primary))"
        strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
        transition={{ duration: 1, ease: 'easeOut' }}
      />
      {points.map((p, i) => (
        <motion.circle
          key={viewMode + '-dot-' + i}
          cx={p.x} cy={p.y} r={data.length > 14 ? 2 : 3}
          fill="hsl(var(--primary))"
          initial={{ scale: 0 }} animate={{ scale: 1 }}
          transition={{ delay: 0.2 + i * 0.04 }}
        />
      ))}
    </svg>
  );
}

// ── Mood Donut Panel ──────────────────────────────────
function MoodPanel({ entries, viewMode }: { entries: Entry[]; viewMode: ViewMode }) {
  const { segments, total } = useMemo(() => {
    const view = VIEWS.find(v => v.key === viewMode)!;
    const cutoff = format(subWeeks(new Date(), view.weeks), 'yyyy-MM-dd');

    const counts: Record<string, number> = {};
    for (const e of entries) {
      if (e.mood && e.date && e.date >= cutoff) {
        counts[e.mood] = (counts[e.mood] || 0) + 1;
      }
    }

    const sorted = Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);

    const totalCount = sorted.reduce((s, [, c]) => s + c, 0);

    return {
      segments: sorted.map(([mood, count], i) => {
        const option = moodOptions.find(
          m => mood.includes(m.emoji) || mood.includes(m.label) || mood.includes(m.id),
        );
        return {
          mood,
          emoji: option?.emoji ?? '📝',
          label: option?.label ?? mood,
          count,
          color: DONUT_COLORS[i % DONUT_COLORS.length],
          ratio: totalCount > 0 ? count / totalCount : 0,
        };
      }),
      total: totalCount,
    };
  }, [entries, viewMode]);

  if (segments.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">
        気分を記録するとここに分布が表示されます
      </p>
    );
  }

  const size = 200;
  const cx = size / 2;
  const cy = size / 2;
  const radius = 70;
  const strokeWidth = 24;
  const circumference = 2 * Math.PI * radius;

  let accumulated = 0;
  const arcs = segments.map(seg => {
    const dash = seg.ratio * circumference;
    const gap = circumference - dash;
    const offset = -accumulated * circumference + circumference * 0.25;
    accumulated += seg.ratio;
    return { ...seg, dash, gap, offset };
  });

  return (
    <div className="flex items-center gap-4 py-2">
      {/* Donut (left) */}
      <div className="relative w-32 h-32 sm:w-36 sm:h-36 shrink-0">
        <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full">
          <circle cx={cx} cy={cy} r={radius} fill="none"
            stroke="currentColor" strokeOpacity={0.06} strokeWidth={strokeWidth} />
          {arcs.map((arc, i) => (
            <motion.circle
              key={arc.mood} cx={cx} cy={cy} r={radius} fill="none"
              stroke={arc.color} strokeWidth={strokeWidth}
              strokeDasharray={`${arc.dash} ${arc.gap}`}
              strokeDashoffset={arc.offset} strokeLinecap="round"
              initial={{ strokeDashoffset: circumference * 0.25 }}
              animate={{ strokeDashoffset: arc.offset }}
              transition={{ duration: 0.6, delay: i * 0.15, ease: 'easeOut' }}
            />
          ))}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold">
            <AnimatedCounter value={total} />
          </span>
          <span className="text-[10px] text-muted-foreground">件</span>
        </div>
      </div>

      {/* Legend (right) */}
      <div className="flex flex-col gap-1.5 min-w-0">
        {arcs.map(arc => (
          <div key={arc.mood} className="flex items-center gap-1.5 text-sm">
            <div className="h-2.5 w-2.5 rounded-full shrink-0"
              style={{ backgroundColor: arc.color }} />
            <span className="shrink-0">{arc.emoji}</span>
            <span className="text-muted-foreground text-xs">{arc.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
